import { useState } from "react";
import { Button, Group, Stack, Tooltip } from "@mantine/core";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import RuleColumnsTable from "../../../../../components/RuleColumnsTable/RuleColumnsTable";
import { useRuleColumnsDataQuery } from "../../../../../components/RuleColumnsTable/useRuleColumnsData";
import ExportFileButton from "../../../../../components/ExportFileButton/ExportFileButton";
import {
  useRuleQuery,
  useAllEntitiesQuery,
} from "../../../../../components/QueryBuilder";
import axios from "../../../../../services/axios/config";
import { getAxiosResponseError } from "../../../../../utils/functions";
import {
  setTestTablePage,
  setTestTableTake,
  triggerTestRun,
  useSelectTestRunNonce,
  useSelectTestTablePage,
  useSelectTestTableTake,
} from "../../../../../redux/features/ruleEditor/ruleEditorSlice";
import { useSelectIsEditingLimit } from "../../../../../redux/features/queryBuilder/queryBuilderSlice";

export default function TestResultsTable({ ruleId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [exportLoading, setExportLoading] = useState(false);

  const testRunNonce = useSelectTestRunNonce();
  const page = useSelectTestTablePage();
  const take = useSelectTestTableTake();
  const enabled = testRunNonce > 0;

  const { data, error } = useRuleColumnsDataQuery({
    ruleId,
    page,
    take,
    nonce: testRunNonce,
    enabled,
  });
  const showExport = enabled && !error && (data?.rows?.length ?? 0) > 0;

  // The Test control must not appear while the canvas is still loading
  // (rule + entity catalog). Same hooks QueryBuilder uses — deduped.
  const { isLoading: ruleLoading, data: rule } = useRuleQuery(ruleId);
  const { isLoading: entitiesLoading } = useAllEntitiesQuery();
  const ready = !ruleLoading && !entitiesLoading && Boolean(rule);

  const mainQueryId = rule?.queries?.[0]?.id;
  const testDisabled = useSelectIsEditingLimit(mainQueryId);
  const testDisabledReason = "Save limit first";

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await axios({
        url: `search/advanced/export?ruleId=${ruleId}&type=EXCEL`,
        method: "POST",
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "file.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      toast.error(getAxiosResponseError(err));
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Stack gap="sm" pb="xl">
      {ready && (
        <Group gap="md" wrap="nowrap" p="sm">
          <Tooltip label={testDisabledReason} disabled={!testDisabled} withArrow>
            <span>
              <Button
                variant="outline"
                disabled={testDisabled}
                onClick={() => {
                  if (testDisabled) return;
                  dispatch(triggerTestRun());
                }}
              >
                {t("common.test")}
              </Button>
            </span>
          </Tooltip>
          {showExport && (
            <ExportFileButton
              type="EXCEL"
              tooltipLabel="Export in excel"
              loading={exportLoading}
              handleDownload={handleExport}
            />
          )}
        </Group>
      )}
      <RuleColumnsTable
        ruleId={ruleId}
        page={page}
        take={take}
        onPageChange={(p) => dispatch(setTestTablePage(p))}
        onTakeChange={(t) => dispatch(setTestTableTake(t))}
        enabled={enabled}
        nonce={testRunNonce}
      />
    </Stack>
  );
}
